import { forwardRef, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock,
  Download,
  ListChecks,
  Percent,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import logoNavy from "@/assets/ajility-logo-horizontal-navy.svg";
import { trackEvent } from "@/lib/analytics";
import { fitCallUrl } from "@/lib/booking";
import { PhoneCallLink } from "@/components/site/PhoneCallLink";
import type { ReportModel, StatIcon, Tone } from "@/lib/tools/calc";

export const STAT_ICONS: Record<StatIcon, typeof Clock> = {
  trend: TrendingUp,
  low: TrendingDown,
  calendar: CalendarDays,
  percent: Percent,
  clock: Clock,
  users: Users,
  list: ListChecks,
  check: CheckCircle2,
  alert: AlertTriangle,
};

export const toneText: Record<Tone, string> = { red: "text-destructive", amber: "text-gold-ink", green: "text-success" };
export const toneBorder: Record<Tone, string> = { red: "border-destructive", amber: "border-gold", green: "border-success" };
const ACTION_ICONS = [Target, CheckCircle2, CalendarDays];

type Props = {
  report: ReportModel;
  firstName: string;
  businessName: string | null;
  chart: ReactNode;
  attribution: string;
  areaDetails?: ReactNode;
};

/** The one page report shown after capture; printed alone on A4 by the Download PDF button. */
export const ToolReport = forwardRef<HTMLElement, Props>(function ToolReport({ report, firstName, businessName, chart, attribution, areaDetails }, ref) {
  const date = new Intl.DateTimeFormat("en-AU", { dateStyle: "long" }).format(new Date());
  const placement = `tool_report_${report.slug.replace(/-/g, "_")}`;
  const t = report.table;
  return (
    <article ref={ref} tabIndex={-1} className={`tool-report ${report.slug === "partner-alignment-check" ? "pac-report" : ""} mx-auto max-w-[900px] rounded-2xl border border-border bg-card p-6 text-foreground shadow-card outline-none sm:p-10`} aria-labelledby="tool-report-title">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-4">
        <img src={logoNavy} alt="Ajility" width={150} height={40} className="h-9 w-auto" />
        <div className="text-right text-xs text-charcoal">
          <p>{date}</p>
          <p>ajility.com.au</p>
        </div>
      </header>
      <p className="eyebrow mt-5">Your {report.toolName} report</p>
      <p className="mt-1 text-sm text-charcoal">
        Prepared for {firstName}
        {businessName ? `, ${businessName}` : ""}
      </p>
      <h2 id="tool-report-title" className="mt-3 font-serif text-3xl leading-tight text-midnight sm:text-4xl">{report.headline}</h2>
      {report.headlineSub ? <p className="mt-1 text-charcoal">{report.headlineSub}</p> : null}

      <div className="mt-5 grid gap-3 sm:grid-cols-3 print:grid-cols-3">
        {report.stats.map((s) => {
          const Icon = STAT_ICONS[s.icon];
          return (
            <div key={s.label} className="rounded-xl border border-border bg-surface p-3">
              <Icon className="h-4 w-4 text-steel" aria-hidden="true" />
              <p className="mt-1 text-xs text-charcoal">{s.label}</p>
              <p className="mt-0.5 font-semibold text-midnight">{s.value}</p>
            </div>
          );
        })}
      </div>

      <p className={`mt-5 border-l-4 pl-4 text-sm leading-relaxed ${toneBorder[report.tone]}`}>{report.verdict}</p>

      <div className="mt-5">{chart}</div>

      {t.rows.length && report.slug !== "weekly-scorecard-builder" ? (
        <div className="tool-report-detail mt-5 overflow-x-auto rounded-xl border border-border" tabIndex={0} role="region" aria-label={t.caption}>
          <table className="w-full border-collapse text-xs">
            <caption className="sr-only">{t.caption}</caption>
            <thead>
              <tr className="bg-surface text-left text-charcoal">
                {t.headers.map((h) => (
                  <th key={h} scope="col" className="p-2 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.rows.map((r, k) => (
                <tr key={k} className={`border-t border-border ${t.highlight?.includes(k) ? "bg-gold/15 font-semibold" : ""} ${t.negative?.includes(k) ? "text-destructive" : ""}`}>
                  {r.map((c, j) => (
                    <td key={j} className="p-2 align-top">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <section className="tool-report-actions mt-6">
        <h3 className="font-serif text-xl text-midnight">Three things to do this month</h3>
        <ol className="mt-3 space-y-2">
          {report.actions.map((a, k) => {
            const Icon = ACTION_ICONS[k] ?? CheckCircle2;
            return (
              <li key={k} className="flex gap-3 text-sm leading-relaxed">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-steel" aria-hidden="true" />
                <span>{a}</span>
              </li>
            );
          })}
        </ol>
      </section>

      {areaDetails}

      <section className="mt-6">
        <h3 className="font-serif text-xl text-midnight">How Ajility helps</h3>
        <p className="mt-2 text-sm leading-relaxed">
          {report.slug === "partner-alignment-check" ? <>
            If the gaps are large, or your partners see things differently, the <a href="/services/partner-alignment" className="font-medium text-midnight underline underline-offset-4">Partner Alignment Review</a> works through all six areas with every partner and ends with a signed-off brief for your own lawyer and accountant.{" "}
            See <Link to="/packages" className="font-medium text-midnight underline underline-offset-4">packages</Link> for how engagements are priced.
          </> : <>{report.serviceText}{" "}<Link to="/packages" className="font-medium text-midnight underline underline-offset-4">See {report.serviceName} on the packages page</Link>.</>}
        </p>
        <p className="mt-2 text-xs text-charcoal">{attribution}</p>
      </section>

      <div className="mt-6 flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-start print:hidden">
        <Link to="/scan" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-midnight px-6 text-sm font-semibold text-frost transition hover:bg-midnight-deep">
          Take the Operating Scan <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <div className="flex flex-col gap-1">
          <a href={fitCallUrl(placement)} target="_blank" rel="noopener noreferrer" data-analytics-cta="Book a fit call" data-analytics-location={placement} className="inline-flex min-h-11 items-center justify-center rounded-full border border-midnight px-6 text-sm font-semibold text-midnight transition hover:bg-surface">
            Book a fit call
          </a>
        </div>
        <button
          type="button"
          onClick={() => {
            trackEvent("tool_pdf_click", { source_tool: report.slug });
            window.print();
          }}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-border px-6 text-sm font-semibold text-midnight transition hover:bg-surface sm:ml-auto"
        >
          <Download className="h-4 w-4" aria-hidden="true" /> Download PDF
        </button>
      </div>
      <PhoneCallLink placement={placement} className="mt-3 inline-block text-sm text-charcoal underline underline-offset-4 hover:text-midnight print:hidden" />
      {report.disclaimer ? <p className="mt-4 text-xs leading-relaxed text-charcoal">{report.disclaimer}</p> : null}
      <p className="mt-4 hidden text-xs text-charcoal print:block">Take the free Operating Scan at ajility.com.au/scan or book a fit call at ajility.com.au/contact.</p>
    </article>
  );
});
